import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ContractReview } from '../../../models/Entities/Contracts/ContractReview';
import { ContractIAImportProcess } from '../../../models/Entities/Contracts/ContractIAImportProcess';
import { EMPTY_FILTERS, FindingFilters, filtersToQuery, isReviewActive, queryToFilters } from './ContractReview.Functions';

// Hooks de la revisión con IA (spec 012).

export const REVIEW_POLL_INTERVAL_MS = 5000;
export const AFTER_IMPORT_WINDOW_MS = 60000;

/**
 * Consulta cada 5 s mientras haya una revisión activa (Queued/Running) o `extraActive` (la ventana de la revisión
 * automática después de importar). Se detiene sola al terminar y al desmontar.
 */
export const useContractReviewPolling = (
  reviews: Pick<ContractReview, 'status'>[] | undefined,
  extraActive: boolean,
  refresh: () => void,
  intervalMs: number = REVIEW_POLL_INTERVAL_MS
) => {
  const shouldPoll = extraActive || (reviews ?? []).some(isReviewActive);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    if (!shouldPoll) return;
    const id = setInterval(() => refreshRef.current(), intervalMs);
    return () => clearInterval(id);
  }, [shouldPoll, intervalMs]);

  return shouldPoll;
};

export interface CompletedReviewNotice {
  review: ContractReview;
  status: 'Completed' | 'Failed' | 'Applied';
}

/**
 * Un único aviso por transición de activa (Queued/Running) a terminada (Completed/Failed/Applied). La revisión de la
 * primera importación se aplica sola (Contracts `AutoApplyAfterImport`): puede pasar de activa a Applied, o de Completed
 * a Applied si la consulta la vio en el medio; las dos dan un aviso `Applied`. Devuelve el último aviso para la región
 * `aria-live`; `onNotice` lo muestra como toast (DEC-010: sólo en el detalle y en la revisión).
 */
export const useReviewCompletionToast = (
  reviews: ContractReview[] | undefined,
  onNotice: (notice: CompletedReviewNotice) => void
): CompletedReviewNotice | undefined => {
  const previous = useRef<Record<string, string>>({});
  const [last, setLast] = useState<CompletedReviewNotice | undefined>(undefined);
  const onNoticeRef = useRef(onNotice);
  onNoticeRef.current = onNotice;

  useEffect(() => {
    (reviews ?? []).forEach(review => {
      const before = previous.current[review.id];
      const wasActive = before === 'Queued' || before === 'Running';
      const applied = review.status === 'Applied' || review.status === 'PartiallyApplied';
      const status: CompletedReviewNotice['status'] | undefined =
        wasActive && (review.status === 'Completed' || review.status === 'Failed')
          ? review.status
          : applied && (wasActive || (before === 'Completed' && review.trigger === 'AfterImport'))
            ? 'Applied'
            : undefined;
      if (status) {
        const notice: CompletedReviewNotice = { review, status };
        setLast(notice);
        onNoticeRef.current(notice);
      }
      previous.current[review.id] = review.status;
    });
  }, [reviews]);

  return last;
};

/**
 * Revisión automática después de importar (US7): cuando la importación pasa de en curso (1|2) a terminada bien (3),
 * abre una ventana de 60 s con polling para encontrar la revisión `AfterImport` de ese proceso. Mismo criterio de
 * éxito que `useIAImportCompletionRefresh` (research R8). Sin revisión a los 60 s, deja de esperar sin error.
 */
export const useAfterImportReview = (
  lastProcess: Pick<ContractIAImportProcess, 'id' | 'status'> | null | undefined,
  reviews: ContractReview[] | undefined,
  windowMs: number = AFTER_IMPORT_WINDOW_MS
): { waiting: boolean; review: ContractReview | undefined } => {
  const previousStatus = useRef<number | undefined>(lastProcess?.status);
  const [processId, setProcessId] = useState<string | undefined>(undefined);
  const [waiting, setWaiting] = useState(false);

  useEffect(() => {
    const before = previousStatus.current;
    const status = lastProcess?.status;
    previousStatus.current = status;
    if ((before === 1 || before === 2) && status === 3 && lastProcess?.id) {
      setProcessId(lastProcess.id);
      setWaiting(true);
    }
  }, [lastProcess?.id, lastProcess?.status]);

  const review = useMemo(
    () => (processId ? (reviews ?? []).find(r => r.trigger === 'AfterImport' && r.importProcessId === processId) : undefined),
    [processId, reviews]
  );

  // Encontrada, o vencida la ventana: se deja de esperar.
  useEffect(() => {
    if (!waiting) return;
    if (review) {
      setWaiting(false);
      return;
    }
    const id = setTimeout(() => setWaiting(false), windowMs);
    return () => clearTimeout(id);
  }, [waiting, review, windowMs]);

  return { waiting, review };
};

/** Filtros de hallazgos ↔ query string (`?scope=&category=&severity=&decision=&result=&patch=&concept=&q=&tab=`). */
export const useFindingFilters = (): [
  FindingFilters,
  (filters: FindingFilters, extra?: Record<string, string | undefined>) => void,
  URLSearchParams,
  (key: string, value?: string) => void
] => {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => queryToFilters(params), [params]);

  const setFilters = useCallback(
    (next: FindingFilters, extra?: Record<string, string | undefined>) => {
      const query = filtersToQuery(next, params);
      Object.entries(extra ?? {}).forEach(([key, value]) => (value === undefined || value === '' ? query.delete(key) : query.set(key, value)));
      setParams(query, { replace: true });
    },
    [params, setParams]
  );

  const setParam = useCallback(
    (key: string, value?: string) => {
      const next = new URLSearchParams(params);
      if (value === undefined || value === '') next.delete(key);
      else next.set(key, value);
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  return [filters ?? EMPTY_FILTERS, setFilters, params, setParam];
};
